import { z } from 'zod'

import {
  pageField,
  queryTerm,
  type SortOf,
  type SortRule,
  termField,
} from '@/components/directory/search'

import type { CustomerDirectoryParams } from './queries'

/** How the directory may be ordered: a field, or `-field` for descending. */
export const SORT_VALUES = [
  'full_name',
  '-full_name',
  'last_activity',
  '-last_activity',
  'created_at',
  '-created_at',
] as const satisfies readonly NonNullable<CustomerDirectoryParams['sort']>[]

/** Last seen first, as the API defaults. Never written to the URL: the directory maps it away. */
export const DEFAULT_SORT = '-last_activity' satisfies CustomerSort

/** The windows the "Last seen" menu offers, in days. */
export const SEEN_WINDOWS = [30, 90, 365] as const

/**
 * Route search params for the customers directory: the term, the filters, the sort and the page.
 *
 * **There is no licence term and there never will be one**, only a licence *standing*: an index
 * over the licence number is what APP 9.1 forbids (ADR 0006), and a search box is the front of an
 * index. Filtering by whether a licence has run out asks a question of each row rather than
 * organising rows by who somebody is, which is the distinction that ADR turns on.
 *
 * Every field falls back to its default on its own, so one malformed value in a shared link drops
 * that value rather than the whole view.
 */
export const customerListSearchSchema = z
  .object({
    q: termField,
    activity: z.enum(['out', 'booked', 'returned', 'enquiry']).optional().catch(undefined),
    // A window typed into the address bar arrives as a string; the router only parses JSON it wrote.
    seen: z.coerce.number().int().min(1).max(3650).optional().catch(undefined),
    licence: z.enum(['missing', 'valid', 'expiring', 'expired']).optional().catch(undefined),
    email: z.boolean().optional().catch(undefined),
    sort: z.enum(SORT_VALUES).optional().catch(undefined),
    page: pageField,
  })
  // A search string that is not an object at all (`?q[]=a&q[]=b`) falls back to the default view.
  .catch({})

export type CustomerListSearch = z.infer<typeof customerListSearchSchema>

export type CustomerSort = SortOf<CustomerListSearch>

/**
 * How the directory sorts. Dates start descending, because most recent first is what anyone
 * sorting by one wants; a name starts ascending. Nothing licence-shaped can order the table.
 */
export const CUSTOMER_SORT: SortRule<CustomerSort> = {
  default: DEFAULT_SORT,
  descendingFirst: ['last_activity', 'created_at'],
  words: { full_name: 'name', last_activity: 'last activity', created_at: 'date added' },
}

/** The rows one page holds. The API's default, repeated here because the pager counts in them. */
export const DIRECTORY_PAGE = 50

/**
 * Search params -> the query the API takes. Defaults are left out, so the default view is a
 * request with no query string and a shared link asks for exactly what it shows.
 */
export function directoryParams(search: CustomerListSearch): CustomerDirectoryParams {
  const params: CustomerDirectoryParams = {}
  // Shorter queries are ignored by the API; not sending one keeps the unfiltered list on screen.
  const q = queryTerm(search)
  if (q) params.q = q
  if (search.activity) params.activity = search.activity
  if (search.seen) params.seen_within_days = search.seen
  if (search.licence) params.licence = search.licence
  if (search.email !== undefined) params.has_email = search.email
  if (search.sort && search.sort !== DEFAULT_SORT) params.sort = search.sort
  if (search.page && search.page > 1) params.offset = (search.page - 1) * DIRECTORY_PAGE
  return params
}

/** How many numbered steps sit either side of the current one before the row starts eliding. */
const NEIGHBOURS = 1

/**
 * The numbers a pager draws: every page when there are few, and the ends plus the neighbourhood
 * of the current one when there are many. `null` is a gap, drawn as an ellipsis rather than as a
 * button nobody can press.
 *
 * It lives beside `directoryParams`, which is the other half of the page arithmetic: that turns
 * a page into an offset, and this decides which pages a reader is offered at all.
 */
export function pageNumbers(page: number, pages: number): (number | null)[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, index) => index + 1)
  const near = new Set([1, pages, page])
  for (let step = 1; step <= NEIGHBOURS; step += 1) {
    if (page - step > 1) near.add(page - step)
    if (page + step < pages) near.add(page + step)
  }
  const shown = [...near].sort((first, second) => first - second)
  return shown.flatMap((number, index) =>
    index > 0 && number - shown[index - 1]! > 1 ? [null, number] : [number],
  )
}
